<template>
  <header class="topbar">
    <div class="topbar-inner">
      <!-- Left Section - Page Title / Breadcrumb -->
      <div class="topbar-left">
        <button class="mobile-menu-btn" @click="$emit('toggle-sidebar')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6H20M4 12H20M4 18H20"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <div class="page-info">
          <!-- Show property indicator when available -->
          <template v-if="propertyInfo && propertyInfo.title">
            <div class="property-indicator">
              <div class="property-indicator-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 9.5L12 4L21 9.5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="property-indicator-title">{{
                propertyInfo.title
              }}</span>
              <span class="property-indicator-separator">·</span>
              <span class="property-indicator-project">{{
                propertyInfo.project
              }}</span>
              <button
                class="property-indicator-close"
                @click="$emit('go-back')"
                title="Volver a Mis Casos"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </template>
          <!-- Default page info -->
          <template v-else>
            <h1 class="page-title">{{ pageTitle }}</h1>
            <p class="page-subtitle">{{ pageSubtitle }}</p>
          </template>
        </div>
      </div>

      <!-- Right Section - User & Actions -->
      <div class="topbar-right">
        <!-- Notifications -->
        <button class="topbar-action" title="Notificaciones">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 7.68629 15.3137 5 12 5C8.68629 5 6 7.68629 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9 17V18C9 19.6569 10.3431 21 12 21C13.6569 21 15 19.6569 15 18V17"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <!-- Settings with Dropdown -->
        <!-- Oculto temporalmente - Solo se usará diseño clásico -->
        <div class="settings-dropdown" v-if="false">
          <button
            class="topbar-action"
            title="Configuración"
            @click="toggleSettingsMenu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path
                d="M19.6224 10.3954L18.5247 7.7448L20 6L18 4L16.2647 5.48295L13.5578 4.36974L12.9353 2H10.981L10.3491 4.40113L7.70441 5.51596L6 4L4 6L5.45337 7.78885L4.3725 10.4463L2 11V13L4.40111 13.6555L5.51575 16.2997L4 18L6 20L7.79116 18.5403L10.397 19.6123L11 22H13L13.6045 19.6132L16.2551 18.5155C16.6969 18.8313 18 20 18 20L20 18L18.5159 16.2494L19.6139 13.598L21.9999 12.9772L22 11L19.6224 10.3954Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <!-- Settings Menu -->
          <div class="settings-menu" v-if="settingsMenuOpen">
            <div class="settings-header">
              <span>Configuración</span>
            </div>
            <div class="settings-section">
              <span class="settings-label">Diseño del portal</span>
              <div class="layout-options">
                <button
                  :class="[
                    'layout-option',
                    { active: layoutMode === 'classic' },
                  ]"
                  @click="changeLayout('classic')"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="4"
                      rx="1"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <rect
                      x="3"
                      y="10"
                      width="18"
                      height="11"
                      rx="1"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                  Clásico
                </button>
                <button
                  :class="[
                    'layout-option',
                    { active: layoutMode === 'modern' },
                  ]"
                  @click="changeLayout('modern')"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="3"
                      width="5"
                      height="18"
                      rx="1"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <rect
                      x="11"
                      y="3"
                      width="10"
                      height="4"
                      rx="1"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <rect
                      x="11"
                      y="10"
                      width="10"
                      height="11"
                      rx="1"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                  Moderno
                </button>
              </div>
            </div>
          </div>
          <!-- Backdrop to close menu -->
          <div
            v-if="settingsMenuOpen"
            class="settings-backdrop"
            @click="settingsMenuOpen = false"
          ></div>
        </div>

        <!-- User Menu -->
        <div class="user-menu" v-if="user">
          <div class="user-avatar">
            {{ user.name ? user.name.charAt(0).toUpperCase() : "U" }}
          </div>
          <div class="user-info">
            <span class="user-name">{{
              user.name ? user.name.split(" ")[0] : "Usuario"
            }}</span>
            <span class="user-role">Cliente</span>
          </div>
        </div>

        <!-- Logout Button -->
        <button class="logout-btn" @click="logout" title="Cerrar sesión">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H15"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M11 12H4M4 12L7 9M4 12L7 15"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: "TopBar",
  props: {
    layoutMode: { type: String, default: "modern" },
    propertyInfo: { type: Object, default: null },
  },
  data() {
    return {
      user: this.$auth.user(),
      settingsMenuOpen: false,
    };
  },
  computed: {
    customer() {
      return this.$store.getters.getCustomer;
    },
    pageTitle() {
      const routeTitles = {
        index: "Mis Casos",
        information: "Mi Perfil",
      };
      return routeTitles[this.$route.name] || "Portal del Cliente";
    },
    pageSubtitle() {
      const routeSubtitles = {
        index: "Gestiona tus casos",
        information: "Administra tu información personal",
      };
      return routeSubtitles[this.$route.name] || "";
    },
  },
  methods: {
    toggleSettingsMenu() {
      this.settingsMenuOpen = !this.settingsMenuOpen;
    },
    changeLayout(mode) {
      this.$emit("change-layout", mode);
      this.settingsMenuOpen = false;
    },
    logout() {
      this.$auth.logout({
        redirect: `/login`,
        success: function (msg) {
          console.error("LOGIN:", msg);
        },
      });
    },
  },
};
</script>

<style scoped>
/* ===== TopBar Styles ===== */
.topbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 50;
  height: 82px;
  display: flex;
  align-items: center;
}

.topbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  max-width: 100%;
  width: 100%;
}

/* Left Section */
.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-menu-btn {
  display: none;
  padding: 8px;
  background: none;
  border: none;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-menu-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.page-info {
  display: flex;
  flex-direction: column;
}

.page-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  line-height: 1.3;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Property Indicator - Text only minimal style */
.property-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  animation: indicatorSlideIn 0.3s ease;
}

@keyframes indicatorSlideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.property-indicator-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-menu, #1f2937);
  border-radius: 8px;
  color: var(--color-menu-text, white);
  flex-shrink: 0;
}

.property-indicator-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  text-transform: capitalize;
  white-space: nowrap;
}

.property-indicator-separator {
  color: #9ca3af;
}

.property-indicator-project {
  font-size: 0.9375rem;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
}

.property-indicator-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 4px;
}

.property-indicator-close:hover {
  color: #374151;
  background: #f3f4f6;
}

/* Right Section */
.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.topbar-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 10px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topbar-action:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Settings Dropdown */
.settings-dropdown {
  position: relative;
}

.settings-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 1000;
  overflow: hidden;
}

.settings-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

.settings-section {
  padding: 16px;
}

.settings-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.layout-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.layout-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.layout-option:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.layout-option.active {
  background: var(--color-btn, #1e40af);
  color: var(--color-btn-text, #ffffff);
  border-color: var(--color-btn, #1e40af);
}

.layout-option.active svg {
  stroke: var(--color-btn-text, #ffffff);
}

.settings-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}

/* User Menu */
.user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  background: #f9fafb;
  border-radius: 30px;
  margin-left: 8px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-btn, #1e40af);
  color: var(--color-btn-text, #ffffff);
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
}

.user-role {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Logout Button */
.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 10px;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;
}

.logout-btn:hover {
  background: #fef2f2;
}

/* Responsive */
@media (max-width: 1024px) {
  .user-info {
    display: none;
  }

  .user-menu {
    padding: 0;
    background: transparent;
  }
}

@media (max-width: 768px) {
  .mobile-menu-btn {
    display: flex;
  }

  .topbar-inner {
    padding: 12px 16px;
  }

  .page-title {
    font-size: 1.125rem;
  }

  .page-subtitle {
    display: none;
  }

  .topbar-action:not(.settings-dropdown .topbar-action) {
    display: none;
  }

  .settings-menu {
    width: 260px;
    right: -8px;
  }
}
</style>
